import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Receipt, Trash2, Loader2, Search, Calendar, Camera } from 'lucide-react';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL || '';
const fmt = (n, c = 'PHP') => new Intl.NumberFormat('en-PH', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(n || 0);

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank', label: 'Bank transfer' },
  { value: 'card', label: 'Card' },
  { value: 'wire', label: 'Wire' },
];

export default function FinanceExpensesPage() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    vendor_id: '', vendor_name: '', description: '', amount: '',
    expense_date: new Date().toISOString().slice(0, 10),
    account_code: '6900', tax_category: 'VAT12_PH',
    payment_method: 'bank', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: exp }, { data: ven }, { data: setup }] = await Promise.all([
        axios.get(`${API}/api/finance/expenses`),
        axios.get(`${API}/api/finance/vendors`),
        axios.get(`${API}/api/finance/setup`),
      ]);
      setItems(exp.items || []);
      setVendors(ven.items || []);
      setAccounts((setup.chart_of_accounts || []).filter(a => ['opex', 'cogs'].includes(a.type)));
      setTaxes(setup.tax_categories || []);
    } catch (err) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.description || !form.amount) {
      toast.error('Description and amount are required');
      return;
    }
    setSaving(true);
    try {
      const v = vendors.find(x => x.id === form.vendor_id);
      const taxCat = taxes.find(t => t.code === form.tax_category);
      const amt = parseFloat(form.amount);
      const taxAmt = taxCat ? +(amt * taxCat.rate / (1 + taxCat.rate)).toFixed(2) : 0;
      await axios.post(`${API}/api/finance/expenses`, {
        ...form,
        amount: amt,
        vendor_name: v?.name || form.vendor_name,
        tax_amount: taxAmt,
      });
      toast.success('Expense recorded');
      setOpen(false);
      setForm({ ...form, description: '', amount: '', notes: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record expense');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/api/finance/expenses/${id}`);
      toast.success('Expense removed');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const filtered = items.filter(e =>
    !search ||
    (e.description || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.vendor_name || '').toLowerCase().includes(search.toLowerCase()),
  );
  const total = filtered.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="page-shell-wide" data-testid="finance-expenses-page">
      <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-gray-400 mb-1">
            <Receipt className="h-3 w-3" /> Finance · Expenses
          </div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Expenses<PageInfoTooltip pageKey="finance-expenses" /></h1>
          <p className="text-sm text-gray-600 mt-1">Direct, day-to-day money out — fuel, software, supplies, meals.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-expense-btn"><Plus className="h-4 w-4 mr-1.5" />Add expense</Button>
      </div>

      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-400" />
          <Input
            placeholder="Search by vendor or description..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8" data-testid="expense-search"
          />
        </div>
        <div className="text-sm text-gray-600">
          {filtered.length} expenses · <span className="font-mono font-bold text-gray-900">{fmt(total)}</span> total
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                  <tr>
                    <th className="text-left p-2.5">Date</th>
                    <th className="text-left p-2.5">Vendor</th>
                    <th className="text-left p-2.5">Description</th>
                    <th className="text-left p-2.5">Account</th>
                    <th className="text-right p-2.5">Amount</th>
                    <th className="text-right p-2.5">Tax</th>
                    <th className="text-center p-2.5 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 200).map(e => (
                    <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`expense-row-${e.id}`}>
                      <td className="p-2.5 text-gray-500 font-mono text-xs">{(e.expense_date || '').slice(0, 10)}</td>
                      <td className="p-2.5">{e.vendor_name || '—'}</td>
                      <td className="p-2.5 text-gray-600">{e.description}</td>
                      <td className="p-2.5"><span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded font-mono">{e.account_code}</span></td>
                      <td className="p-2.5 text-right font-mono">{fmt(e.amount, e.currency)}</td>
                      <td className="p-2.5 text-right font-mono text-gray-500">{fmt(e.tax_amount, e.currency)}</td>
                      <td className="p-2.5 text-center">
                        <button onClick={() => remove(e.id)} className="text-gray-300 hover:text-rose-600 transition" data-testid={`expense-delete-${e.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="p-10 text-center text-sm text-gray-400">No expenses recorded.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add expense modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="expense-modal">
          <DialogHeader><DialogTitle>Record an expense</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {/* v2.5.857 — Scan receipt with camera / file */}
            <div className="p-3 bg-violet-50 border border-violet-200 rounded-md">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-violet-800 font-medium">
                <Camera className="h-4 w-4" />
                {scanning ? 'Scanning receipt…' : 'Scan a receipt (auto-fill fields)'}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setScanning(true);
                    const fd = new FormData();
                    fd.append('file', file);
                    try {
                      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/finance/expenses/scan-receipt`, fd, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                      });
                      const p = data.parsed || {};
                      setForm(prev => ({
                        ...prev,
                        expense_date: p.date || prev.expense_date,
                        description: p.vendor_name ? `${p.vendor_name}${p.line_items?.length ? ' — ' + p.line_items.map(i => i.description).join(', ') : ''}` : prev.description,
                        amount: p.total ? String(p.total) : prev.amount,
                        payment_method: p.payment_method && p.payment_method !== 'unknown' ? p.payment_method : prev.payment_method,
                        notes: `Scanned receipt${p.confidence ? ` (conf: ${(p.confidence * 100).toFixed(0)}%)` : ''}${p.category ? ` · category: ${p.category}` : ''}${p.currency ? ` · ${p.currency}` : ''}`,
                      }));
                      toast.success('Receipt scanned — please review');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'OCR failed');
                    } finally { setScanning(false); }
                    e.target.value = '';
                  }}
                  data-testid="expense-scan-receipt"
                />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date</Label>
                <div className="relative">
                  <Calendar className="h-3.5 w-3.5 absolute left-2 top-2.5 text-gray-400" />
                  <Input type="date" value={form.expense_date} onChange={e => setForm({ ...form, expense_date: e.target.value })} className="pl-8" data-testid="expense-date" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Vendor</Label>
                <Select value={form.vendor_id} onValueChange={v => setForm({ ...form, vendor_id: v })}>
                  <SelectTrigger data-testid="expense-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Description *</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What was this for?" data-testid="expense-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount (tax-inclusive) *</Label>
                <Input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" />
              </div>
              <div>
                <Label className="text-xs">Payment method</Label>
                <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Account (expense category)</Label>
                <Select value={form.account_code} onValueChange={v => setForm({ ...form, account_code: v })}>
                  <SelectTrigger data-testid="expense-account"><SelectValue /></SelectTrigger>
                  <SelectContent>{accounts.map(a => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Tax category</Label>
                <Select value={form.tax_category} onValueChange={v => setForm({ ...form, tax_category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{taxes.map(t => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="save-expense-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save expense'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
